/**
 * The one matching engine (piece 24, Card transaction matching): amount plus
 * counterparty name resolved through the Company record, three confidence
 * tiers a bookkeeper tunes, and the reasoning shown per match.
 *
 * 🔨 One matcher serves bank-account reconciliation, credit-card-account
 * reconciliation, expense matching and Bill payment matching alike. No second
 * matching mechanism exists, so every rule a caller needs is here and nothing
 * scores a candidate on its own.
 *
 * 🔨 Nothing here posts. A tier is a proposal; a person confirms it, and Low
 * is never auto-posted at all.
 */
import { cents, formatCurrency } from '../money';

/** Settings › Accounting › Match Confidence Tiers: the three rows a bookkeeper tunes (settled 2026-09-14). */
export const MATCH_WINDOW_DAYS_KEY = 'matching.postingDateWindowDays';
export const MATCH_WINDOW_DAYS_DEFAULT = 3;
export const MATCH_AMOUNT_TOLERANCE_KEY = 'matching.amountToleranceCents';
export const MATCH_AMOUNT_TOLERANCE_DEFAULT = 0;
export const MATCH_NAME_SIMILARITY_KEY = 'matching.nameSimilarityPercent';
export const MATCH_NAME_SIMILARITY_DEFAULT = 80;

export const MATCH_TIER_KEYS: readonly string[] = [MATCH_WINDOW_DAYS_KEY, MATCH_AMOUNT_TOLERANCE_KEY, MATCH_NAME_SIMILARITY_KEY];

export interface MatchTiers {
  postingDateWindowDays: number;
  amountToleranceCents: number;
  nameSimilarityPercent: number;
}

export const MATCH_TIERS_DEFAULT: MatchTiers = {
  postingDateWindowDays: MATCH_WINDOW_DAYS_DEFAULT,
  amountToleranceCents: MATCH_AMOUNT_TOLERANCE_DEFAULT,
  nameSimilarityPercent: MATCH_NAME_SIMILARITY_DEFAULT,
};

const wholeNumber = (value: unknown): number | null => (typeof value === 'number' && Number.isInteger(value) ? value : null);

/** 🔨 A window is whole days and never negative: a negative window matches nothing and reads as a disabled matcher. */
export function matchWindowProblem(value: unknown): string | null {
  const days = wholeNumber(value);
  if (days === null || days < 0) return 'the posting-date window is a whole number of days, zero or more';
  if (days > 90) return 'a posting-date window above 90 days matches almost anything, which is not a High match';
  return null;
}

/** 🔨 A tolerance is whole cents and never negative; zero is the seeded value and means the amounts agree exactly. */
export function matchToleranceProblem(value: unknown): string | null {
  const cents = wholeNumber(value);
  if (cents === null || cents < 0) return 'the amount tolerance is an amount of money, zero or more';
  return null;
}

/** 🔨 A similarity is a percentage: 0 makes every name a match and 100 asks for the same string. */
export function matchSimilarityProblem(value: unknown): string | null {
  const percent = wholeNumber(value);
  if (percent === null || percent < 0 || percent > 100) return 'the name similarity is a whole percentage between 0 and 100';
  return null;
}

/** The three rows read together, each falling back to its seeded figure where the row is unset. */
export function matchTiersFrom(values: Record<string, unknown>): MatchTiers {
  return {
    postingDateWindowDays: wholeNumber(values[MATCH_WINDOW_DAYS_KEY]) ?? MATCH_WINDOW_DAYS_DEFAULT,
    amountToleranceCents: wholeNumber(values[MATCH_AMOUNT_TOLERANCE_KEY]) ?? MATCH_AMOUNT_TOLERANCE_DEFAULT,
    nameSimilarityPercent: wholeNumber(values[MATCH_NAME_SIMILARITY_KEY]) ?? MATCH_NAME_SIMILARITY_DEFAULT,
  };
}

// ---- the name signal ----

const normalise = (value: string): string => value.toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();

/**
 * How alike two counterparty names are, as a whole percentage. The feed writes
 * a name the bank chose — `SQ *ALLIED PANEL 0042` for `Allied Panel Co` — so
 * the measure is over words rather than characters: the share of the shorter
 * name's words that the longer one also carries, with a word counting when the
 * other side has one that starts with it.
 */
export function nameSimilarityPercent(feedDescription: string, counterparty: string): number {
  const left = normalise(feedDescription ?? '').split(' ').filter((word) => word.length > 2);
  const right = normalise(counterparty ?? '').split(' ').filter((word) => word.length > 2);
  if (left.length === 0 || right.length === 0) return 0;
  const [shorter, longer] = right.length <= left.length ? [right, left] : [left, right];
  const hits = shorter.filter((word) => longer.some((other) => other === word || other.startsWith(word) || word.startsWith(other))).length;
  return Math.round((hits / shorter.length) * 100);
}

// ---- the tiers ----

export const MATCH_TIERS = ['High', 'Medium', 'Low'] as const;
export type MatchTier = (typeof MATCH_TIERS)[number];

/** One thing a feed line could be: a keyed payment, an expense line waiting for its charge, or an unpaid invoice a deposit settles (decision 1200). */
export interface MatchCandidate {
  id: string;
  kind: 'Payment' | 'ExpenseLine' | 'Invoice';
  /** Positive cents, whichever direction the money went; the feed's own sign is not the candidate's. */
  amountCents: number;
  /** The candidate's own date, as YYYY-MM-DD. */
  onDate: string;
  /** The counterparty resolved through the Company record, which is the matcher's primary signal beside the amount. */
  counterpartyName: string | null;
}

export interface FeedLineFacts {
  amountCents: number;
  postedOn: string;
  description: string;
}

export interface ScoredCandidate extends MatchCandidate {
  tier: MatchTier;
  /** 🔨 The reasoning is shown per match, so a person confirming reads why rather than trusting a badge. */
  reason: string;
  amountAgrees: boolean;
  withinWindow: boolean;
  namePercent: number;
  dayGap: number;
}

const daysBetween = (left: string, right: string): number => Math.round(Math.abs(Date.parse(`${left}T00:00:00Z`) - Date.parse(`${right}T00:00:00Z`)) / 86_400_000);

/**
 * Every candidate scored against one feed line. 🔨 High is one candidate whose
 * amount is inside the tolerance and whose posting date is inside the window;
 * where several candidates answer that, each is Medium, because a
 * one-click confirmation over an ambiguous pair is the wrong click made easy.
 * Medium is also the same amount with a name at or above the similarity
 * percent. Low is everything else and is never auto-posted.
 */
export function scoreCandidates(line: FeedLineFacts, candidates: readonly MatchCandidate[], tiers: MatchTiers = MATCH_TIERS_DEFAULT): ScoredCandidate[] {
  const facts = candidates.map((candidate) => {
    const dayGap = daysBetween(line.postedOn, candidate.onDate);
    const amountAgrees = Math.abs(Math.abs(line.amountCents) - Math.abs(candidate.amountCents)) <= tiers.amountToleranceCents;
    return { candidate, dayGap, amountAgrees, withinWindow: dayGap <= tiers.postingDateWindowDays, namePercent: nameSimilarityPercent(line.description, candidate.counterpartyName ?? '') };
  });
  const exact = facts.filter((row) => row.amountAgrees && row.withinWindow);
  return facts
    .map((row) => {
      const base = { ...row.candidate, amountAgrees: row.amountAgrees, withinWindow: row.withinWindow, namePercent: row.namePercent, dayGap: row.dayGap };
      if (row.amountAgrees && row.withinWindow) {
        return exact.length === 1
          ? { ...base, tier: 'High' as MatchTier, reason: `the amount agrees and the date is ${row.dayGap} day(s) apart, inside the ${tiers.postingDateWindowDays}-day window` }
          : { ...base, tier: 'Medium' as MatchTier, reason: `${exact.length} candidates agree on the amount inside the window, so no one of them is a one-click match` };
      }
      if (row.amountAgrees && row.namePercent >= tiers.nameSimilarityPercent) {
        return { ...base, tier: 'Medium' as MatchTier, reason: `the amount agrees and the name scores ${row.namePercent}%, but the date is ${row.dayGap} day(s) apart` };
      }
      const why = row.amountAgrees ? `the amount agrees, the date is ${row.dayGap} day(s) apart and the name scores ${row.namePercent}%` : `the amount differs and the name scores ${row.namePercent}%`;
      return { ...base, tier: 'Low' as MatchTier, reason: `${why}: a partial signal, never posted without somebody deciding` };
    })
    .sort((a, b) => MATCH_TIERS.indexOf(a.tier) - MATCH_TIERS.indexOf(b.tier) || a.dayGap - b.dayGap);
}

/** 🔨 Low is never auto-posted, and no tier posts without a person confirming it; the refusal says which of the two applies. */
export function autoPostRefusal(tier: MatchTier): string | null {
  if (tier === 'Low') return 'a Low match is a partial signal and is never posted automatically; confirm it by hand or leave it';
  return null;
}

// ---- a deposit that settles an unpaid invoice (decision 1200) ----

/** The one unpaid invoice a deposit is proposed against on its own feed row, with what the one engine scored it. */
export interface InvoiceProposal {
  invoiceId: string;
  invoiceNumber: string | null;
  companyName: string | null;
  /** What the invoice still owes, in cents, which is what the allocation takes at most. */
  openBalanceCents: number;
  tier: MatchTier;
  reason: string;
}

/**
 * 🔒 The invoice a deposit is proposed against (decision 1200): the one scored
 * candidate whose amount agrees inside the tolerance and whose customer's name
 * scores at or above the similarity percent. 🔨 Two invoices answering both
 * propose neither, because one press over an ambiguous pair records the wrong
 * money with no error.
 */
export function pickInvoiceProposal(scored: readonly ScoredCandidate[], tiers: MatchTiers = MATCH_TIERS_DEFAULT): ScoredCandidate | null {
  const answering = scored.filter((candidate) => candidate.kind === 'Invoice' && candidate.amountAgrees && candidate.namePercent >= tiers.nameSimilarityPercent);
  return answering.length === 1 ? answering[0]! : null;
}

/** 🔨 Only money arriving settles a customer's invoice; a line that left the account is refused by name. */
export function invoiceMatchProblem(amountCents: number): string | null {
  if (amountCents > 0) return null;
  return 'this feed line is money that left the account, and only a deposit settles a customer invoice';
}

/**
 * 🔒 Unmatch reverses what the Match press recorded, and nothing else
 * (decision 1200). 🔨 A line matched to a payment somebody keyed by hand is
 * refused, because voiding that payment would reverse money the press never
 * recorded.
 */
export function unmatchProblem(line: { matchStatus: string; matchedPaymentId: string | null; matchedInvoiceId: string | null; clearedInReconciliationId: string | null }): string | null {
  if (line.clearedInReconciliationId) return clearedRowRefusal('unmatch');
  if (line.matchStatus !== 'Matched' || !line.matchedPaymentId) return 'this feed line is not matched to a payment, so there is nothing to unmatch';
  if (!line.matchedInvoiceId) return 'this feed line is matched to a payment somebody recorded by hand, and Unmatch reverses only a payment the Match button recorded';
  return null;
}

/** The void reason the payment carries, so the payment's own history says where its void came from. */
export function unmatchVoidReason(description: string, postedOn: string): string {
  return `Unmatched from the bank feed line ${description} of ${postedOn}; the deposit returned to Unmatched.`;
}

// ---- the rules a bookkeeper writes ----

export const BANK_RULE_FIELDS = ['Description', 'Amount', 'Counterparty'] as const;
export type BankRuleField = (typeof BANK_RULE_FIELDS)[number];
export const BANK_RULE_OPERATORS = ['Equals', 'Contains', 'Between'] as const;
export type BankRuleOperator = (typeof BANK_RULE_OPERATORS)[number];

export interface BankRuleFacts {
  id: string;
  bankAccountId: string | null;
  matchField: BankRuleField;
  operator: BankRuleOperator;
  /** The text for Description and Counterparty, the amount in cents for Amount, and two amounts joined by `..` for Between. */
  value: string;
  targetAccountId: string;
  targetPayeeCompanyId: string | null;
  priority: number;
  isActive: boolean;
}

/**
 * 🔨 Between takes two amounts joined by `..`; every other operator takes one value, and an empty value matches everything.
 * The refusals name cents because the route takes cents; the Bank Rule window's money boxes take dollars and never reach them (T3648 review).
 */
export function bankRuleProblem(rule: { matchField: string; operator: string; value: string; targetAccountId?: string | null }): string | null {
  if (!(BANK_RULE_FIELDS as readonly string[]).includes(rule.matchField)) return `a rule matches on ${BANK_RULE_FIELDS.join(', ')}; ${rule.matchField || 'nothing'} is not one of them`;
  if (!(BANK_RULE_OPERATORS as readonly string[]).includes(rule.operator)) return `a rule compares with ${BANK_RULE_OPERATORS.join(', ')}; ${rule.operator || 'nothing'} is not one of them`;
  if (!rule.value?.trim()) return 'a rule names what it compares against; an empty value would match every line on the account';
  if (!rule.targetAccountId) return 'a rule names the account its match posts to; choose the account a matching line posts to';
  if (rule.operator === 'Between') {
    if (rule.matchField !== 'Amount') return 'Between compares amounts, so it belongs to an Amount rule';
    const pair = amountRange(rule.value);
    if (!pair) return 'Between takes two amounts in cents joined by two dots, low first, as in 1000..2500';
    if (pair[0] > pair[1]) return 'Between takes the lower amount first';
  }
  if (rule.matchField === 'Amount' && rule.operator !== 'Between' && !Number.isInteger(Number(rule.value.trim()))) {
    return 'an Amount rule compares against a whole number of cents, as in 1000';
  }
  return null;
}

function amountRange(value: string): [number, number] | null {
  const parts = value.split('..').map((part) => Number(part.trim()));
  if (parts.length !== 2 || parts.some((part) => !Number.isInteger(part))) return null;
  return [parts[0]!, parts[1]!];
}

/** Whether one rule matches one line; an inactive rule matches nothing and a rule scoped to another account matches nothing. */
export function ruleMatches(rule: BankRuleFacts, line: FeedLineFacts & { bankAccountId: string; counterpartyName?: string | null }): boolean {
  if (!rule.isActive) return false;
  if (rule.bankAccountId && rule.bankAccountId !== line.bankAccountId) return false;
  if (rule.matchField === 'Amount') {
    const amount = Math.abs(line.amountCents);
    if (rule.operator === 'Between') {
      const pair = amountRange(rule.value);
      return pair !== null && amount >= pair[0] && amount <= pair[1];
    }
    const target = Number(rule.value.trim());
    return Number.isInteger(target) && amount === Math.abs(target);
  }
  const haystack = normalise(rule.matchField === 'Counterparty' ? (line.counterpartyName ?? '') : line.description);
  const needle = normalise(rule.value);
  if (!needle) return false;
  return rule.operator === 'Equals' ? haystack === needle : haystack.includes(needle);
}

/**
 * 🔨 A rule is evaluated before the AI matcher, in priority order, and the
 * match records the rule that won (decision 0216). Two rules matching one line
 * apply in priority order, lowest first, and the second never runs.
 */
export function pickBankRule(rules: readonly BankRuleFacts[], line: FeedLineFacts & { bankAccountId: string; counterpartyName?: string | null }): BankRuleFacts | null {
  return [...rules].sort((a, b) => a.priority - b.priority || a.id.localeCompare(b.id)).find((rule) => ruleMatches(rule, line)) ?? null;
}

/** 🔨 A rule match is stamped High with the rule as its named reason, and still takes the one-click confirmation every High match takes. */
export function ruleMatchReason(rule: BankRuleFacts): string {
  return `the rule matching ${rule.matchField} ${rule.operator} “${rule.value}” at priority ${rule.priority}; a rule bypasses no review, because no tier posts without a person confirming it`;
}

// ---- the states a person sets by hand ----

/**
 * 🔨 `Excluded` is a duplicate feed line only. A line nothing duplicates is
 * refused, because Excluded is where a line goes to stop counting, and a real
 * transaction hidden there is money the reconciliation will never find.
 *
 * 🔨 A card payment this system wrote itself counts as the duplicate on the
 * amount and the day alone (decision 0543). The aggregator describes the charge
 * in the bank's own words and the run described it by the bills it settled, so
 * demanding the same description refused the one exclusion that was always
 * right (found in review, 2026-09-18).
 */
export function excludeProblem(duplicateCount: number, syntheticCounterpartCount = 0): string | null {
  if (duplicateCount > 0 || syntheticCounterpartCount > 0) return null;
  return 'Excluded is for a duplicate feed line only; nothing else on this account carries the same date and amount, so this line is not one';
}

/** 🔨 A transfer is between two of the company's own accounts, and the two feed lines are the same money seen twice. */
export function bankTransferProblem(from: { bankAccountId: string; amountCents: number } | null, to: { bankAccountId: string; amountCents: number } | null): string | null {
  if (!from || !to) return 'a transfer names both feed lines: the one that left an account and the one that arrived in another';
  if (from.bankAccountId === to.bankAccountId) return 'a transfer moves money between two different accounts; both of these lines are on the same one';
  if (from.amountCents >= 0 || to.amountCents <= 0) return 'a transfer pairs the withdrawal with the deposit; one line is negative and the other positive';
  if (Math.abs(from.amountCents) !== to.amountCents) return `a transfer is the same money seen twice, so the two lines agree on the amount; these differ by ${Math.abs(Math.abs(from.amountCents) - to.amountCents)} cents`;
  return null;
}

/** 🔨 Categorizing writes one balanced entry against the bank's own account and the account the person chose, and nothing else. */
export function categorizeProblem(accountId: string | null | undefined, bankGlAccountId: string): string | null {
  if (!accountId?.trim()) return 'categorizing a feed line names the account it posts to';
  if (accountId === bankGlAccountId) return 'a feed line posts against the bank account and one other account; naming the bank on both sides posts nothing';
  return null;
}

/**
 * 🔒 One line of a split categorization (decision 8821): an account, its share of the feed line in whole cents signed as
 * the feed line is, so a deposit's shares are positive and a withdrawal's negative, and an optional company.
 */
export interface CategorizeLine {
  accountId: string;
  amountCents: number;
  companyId?: string | null;
}

/** 🔒 What a split's lines still leave of the feed line (decision 8821), signed as the feed line is; zero when they meet. */
export function splitRemainderCents(feedLineCents: number, lines: readonly { amountCents: number | null }[]): number {
  return feedLineCents - lines.reduce((sum, line) => sum + (line.amountCents ?? 0), 0);
}

/**
 * 🔨 A split posts one entry: two lines or more, each naming an account other than the bank's and a share in whole
 * cents that is not zero, the shares summing to the feed line (decision 8821). Null where it may post.
 */
export function categorizeSplitProblem(lines: readonly { accountId?: string | null; amountCents?: number | null }[], bankGlAccountId: string, feedLineCents: number): string | null {
  if (lines.length < 2) return 'a split names two lines or more; one account is categorized without a split';
  if (lines.some((line) => !line.accountId?.trim())) return 'every line of a split names the account it posts to';
  if (lines.some((line) => line.accountId === bankGlAccountId)) return 'a line of a split names the bank account itself, which posts nothing';
  if (lines.some((line) => typeof line.amountCents !== 'number' || !Number.isInteger(line.amountCents) || line.amountCents === 0)) return 'every line of a split carries an amount in whole cents, and none is zero';
  const remainder = splitRemainderCents(feedLineCents, lines.map((line) => ({ amountCents: line.amountCents ?? 0 })));
  if (remainder === 0) return null;
  return `the lines of a split sum to the feed line’s ${formatCurrency(cents(feedLineCents))}; they differ from it by ${formatCurrency(cents(Math.abs(remainder)))}`;
}

/**
 * 🔨 A feed line that already carries a state is refused by name by every act
 * that would give it a second one. A `Matched` line's money reached the ledger
 * through its Payment's own entry, so categorising it posts the same money
 * twice with no error and nothing to see until the trial balance.
 */
export function settledRowRefusal(matchStatus: string): string | null {
  if (matchStatus === 'Matched') {
    return 'this feed line is already matched to a payment, and that payment is what posted the money; categorizing, excluding or transferring it now would post the same money a second time';
  }
  if (matchStatus === 'Categorized') return 'this feed line is already categorized; reverse its entry before categorizing it again';
  if (matchStatus === 'Transfer') return 'this feed line is already one half of a bank transfer, and the transfer posted the money; a second state on it would post the same money twice';
  if (matchStatus === 'Excluded') return 'this feed line is excluded as a duplicate, so it counts nowhere and takes no other state';
  return null;
}

/** 🔨 A row cleared in a Completed reconciliation is locked the way a posted entry is, so every re-touch is refused by name. */
/**
 * 🔨 A line the bank withdrew records nothing new (decision 1541): no statement
 * proves it, so a match, a confirmed match, a category, a rule, a transfer or
 * an exclusion written against it would record money the bank says never moved.
 * A match already standing on it is undone through Unmatch, which this does not
 * refuse.
 */
export function withdrawnRowRefusal(): string {
  return 'the bank withdrew this feed line, so no money moved on it; nothing can be matched, categorized, transferred or excluded against it, and a match already on it is undone with Unmatch';
}

export function clearedRowRefusal(action: 'unmatch' | 're-categorise' | 'delete'): string {
  return `this row was cleared in a completed reconciliation, so it cannot be ${action === 'unmatch' ? 'unmatched' : action === 'delete' ? 'deleted' : 're-categorised'} until that reconciliation is reopened with a reason`;
}

// ---- the statement a period is proved against ----

/**
 * 🔨 The difference is the statement's ending balance less the cleared book
 * balance, and `Finish` is refused while it is not zero (decision 0215).
 * An outstanding check, a duplicated feed line, a missing deposit and a feed
 * that stopped syncing all show as a difference, which is the point.
 */
export function reconciliationDifference(statementEndingBalanceCents: number, clearedBookBalanceCents: number): number {
  return statementEndingBalanceCents - clearedBookBalanceCents;
}

export function finishProblem(difference: number): string | null {
  if (difference === 0) return null;
  return `the statement and the cleared rows differ by ${difference} cents, and a reconciliation is finished at zero; an outstanding check, a duplicated feed line or a missing deposit all read as this difference`;
}

/** 🔨 A reopen records its reason as an `AuditEvent`, like a period reopen (decision 0196), so it names one. */
export function reopenProblem(reason: string | null | undefined): string | null {
  return reason?.trim() ? null : 'reopening a completed reconciliation names why, because the reopen is recorded the way a period reopen is';
}

/** 🔨 A statement names the day it ends and the balance it ends at; neither is inferred from the feed. */
export function reconciliationOpenProblem(statementEndDate: string | null | undefined, statementEndingBalanceCents: unknown): string | null {
  if (!statementEndDate || !/^\d{4}-\d{2}-\d{2}$/.test(statementEndDate)) return 'a reconciliation names the day the statement ends, as YYYY-MM-DD; enter the statement’s closing date';
  if (typeof statementEndingBalanceCents !== 'number' || !Number.isInteger(statementEndingBalanceCents)) return 'a reconciliation names the balance the statement ends at, in whole cents; enter the closing balance the statement shows';
  return null;
}
